import { useState } from "react";
import { articleComments, defaultComments, type CommentItem } from "@/mocks/comments";

interface CommentsSectionProps {
  articleSlug: string;
}

export default function CommentsSection({ articleSlug }: CommentsSectionProps) {
  const comments: CommentItem[] = articleComments[articleSlug] || defaultComments;
  const [commentList, setCommentList] = useState<CommentItem[]>(comments);
  const [newComment, setNewComment] = useState("");
  const [nickname, setNickname] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "submitted">("idle");
  const [inputFocused, setInputFocused] = useState(false);

  const handleLike = (id: string) => {
    setCommentList((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        return {
          ...c,
          liked: !c.liked,
          likes: c.liked ? c.likes - 1 : c.likes + 1,
        };
      })
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !nickname.trim()) return;

    const newItem: CommentItem = {
      id: `user-${Date.now()}`,
      author: nickname.trim(),
      avatarInitial: nickname.trim().charAt(0).toUpperCase(),
      avatarBg: "bg-primary-500",
      date: new Date().toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).replace(/\//g, "-"),
      content: newComment.trim(),
      likes: 0,
      liked: false,
    };

    setCommentList((prev) => [newItem, ...prev]);
    setNewComment("");
    setSubmitStatus("submitted");
    setTimeout(() => setSubmitStatus("idle"), 3000);
    setInputFocused(false);
  };

  const formatLikes = (num: number): string => {
    if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return String(num);
  };

  return (
    <section className="max-w-[780px] mx-auto px-6 md:px-10 pb-14 md:pb-18">
      {/* Comment count header */}
      <div className="flex items-center gap-3 mb-8 pt-8 border-t border-background-200/70">
        <h2 className="font-heading text-xl font-bold text-foreground-950">
          评论区
        </h2>
        <span className="text-sm text-foreground-400 bg-background-100 px-2.5 py-1 rounded-full">
          {commentList.length}
        </span>
      </div>

      {/* Comment input */}
      <div className="mb-10">
        {submitStatus === "submitted" ? (
          <div className="flex items-center gap-3 p-5 bg-primary-50 border border-primary-200 rounded-xl">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-full shrink-0">
              <i className="ri-check-line text-background-50 dark:text-foreground-950"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-primary-700">评论发表成功！</p>
              <p className="text-xs text-primary-500">你的评论已出现在评论区顶部</p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className={`bg-background-100 border rounded-xl p-4 md:p-5 transition-all duration-300 ${
              inputFocused
                ? "border-primary-300 shadow-[0_0_0_4px_rgba(var(--primary-400)/0.1)]"
                : "border-background-200/70"
            }`}
          >
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-user-line text-primary-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="你的昵称"
                  required
                  maxLength={20}
                  onFocus={() => setInputFocused(true)}
                  className="w-full h-9 px-0 text-sm bg-transparent border-none text-foreground-950 placeholder:text-foreground-400 outline-none font-medium"
                />
                <textarea
                  value={newComment}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setNewComment(e.target.value);
                  }}
                  placeholder="说说你的看法..."
                  required
                  maxLength={500}
                  rows={2}
                  onFocus={() => setInputFocused(true)}
                  className="w-full px-0 py-1.5 text-sm bg-transparent border-none text-foreground-800 placeholder:text-foreground-400 outline-none resize-none"
                ></textarea>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-foreground-400">
                    {newComment.length}/500
                  </span>
                  <button
                    type="submit"
                    disabled={!newComment.trim() || !nickname.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-400 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line text-sm"></i>
                    发表
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Comments list */}
      <div className="space-y-5">
        {commentList.map((comment) => (
          <div
            key={comment.id}
            className="flex gap-3 group"
          >
            {/* Avatar */}
            <div
              className={`w-10 h-10 rounded-full ${comment.avatarBg} flex items-center justify-center shrink-0`}
            >
              <span className="text-sm font-bold text-background-50 dark:text-foreground-950">
                {comment.avatarInitial}
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-foreground-950">
                  {comment.author}
                </span>
                <span className="text-xs text-foreground-400">{comment.date}</span>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-2.5">
                {comment.content}
              </p>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => handleLike(comment.id)}
                  className={`flex items-center gap-1 text-xs transition-colors duration-200 cursor-pointer ${
                    comment.liked
                      ? "text-primary-500 font-medium"
                      : "text-foreground-400 hover:text-foreground-600"
                  }`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i
                      className={`text-xs ${
                        comment.liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"
                      }`}
                    ></i>
                  </div>
                  {comment.likes > 0 ? formatLikes(comment.likes) : "赞"}
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-foreground-400 hover:text-foreground-600 transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-chat-1-line text-xs"></i>
                  </div>
                  回复
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load more */}
      {commentList.length >= 4 && (
        <div className="text-center mt-8">
          <button
            type="button"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 hover:bg-background-200 border border-background-200/70 text-sm text-foreground-600 hover:text-foreground-950 rounded-lg font-medium transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-down-line"></i>
            查看更多评论
          </button>
        </div>
      )}
    </section>
  );
}